import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Table, Row, Col, Card, CardBody, Input, Badge,
    Pagination, PaginationItem, PaginationLink, Label, Form, Progress,
    FormGroup
} from "reactstrap";
import { FiShoppingCart, FiBox, FiTrendingUp, FiRefreshCcw, FiUser, FiMapPin } from "react-icons/fi";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell, Legend
} from "recharts";
import Select from "react-select"; // Machan Addon
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";

// MUI Imports - Dashboard ref addon
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const PurchaseReportList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [rawResponse, setRawResponse] = useState(null);

    // Filters state addon from dashboard ref
    const [filters, setFilters] = useState({
        search: "",
        from_date: dayjs(), // MUI compatible
        to_date: dayjs(),
        page: 1,
        limit: 10,
        sort: "DESC",
        filter_period: { value: "today", label: "Today" } // select-react addon
    });

    const filterOptions = [
        { value: "today", label: "Today" },
        { value: "week", label: "Last Week" },
        { value: "month", label: "Last Month" },
        { value: "3months", label: "Last 3 Months" },
        { value: "year", label: "Full Year" },
        { value: "custom", label: "Custom Range" }
    ];

    const loadReports = async (currentFilters = filters) => {
        dispatch(loadingTrue());
        try {
            const queryParams = new URLSearchParams({
                search: currentFilters.search.length >= 2 ? currentFilters.search : "",
                from_date: currentFilters.from_date ? currentFilters.from_date.format('YYYY-MM-DD') : "",
                to_date: currentFilters.to_date ? currentFilters.to_date.format('YYYY-MM-DD') : "",
                page: currentFilters.page,
                limit: currentFilters.limit,
                sort: currentFilters.sort,
                filter: currentFilters.filter_period.value
            }).toString();

            const res = await Handler({ method: "GET", url: `/report/purchase_report.php?${queryParams}` });
            if (res?.success) {
                setRawResponse(res);
            }
        } catch (err) {
            toast.error("Sync Failed");
        } finally {
            dispatch(loadingFalse());
        }
    };

    useEffect(() => {
        if (filters.filter_period.value !== 'custom') {
            loadReports();
        }
    }, [filters.page, filters.sort]);

    const handlePeriodChange = (selectedOption) => {
        const type = selectedOption.value;
        let from = dayjs();
        let to = dayjs();

        if (type === 'week') from = dayjs().subtract(7, 'day');
        else if (type === 'month') from = dayjs().subtract(1, 'month');
        else if (type === '3months') from = dayjs().subtract(3, 'month');

        const newFilters = { ...filters, filter_period: selectedOption, from_date: from, to_date: to, page: 1 };
        setFilters(newFilters);

        if (type !== 'custom') {
            loadReports(newFilters);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setFilters({ ...filters, page: 1 });
        loadReports();
    };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            minHeight: "45px",
            boxShadow: "none",
            '&:hover': { border: '1px solid #f16436' }
        })
    };

    // 🔥 Chart Logic: Top 5 Farmers (Strictly Maintained)
    const chartData = useMemo(() => {
        const items = rawResponse?.data || [];
        return items.slice(0, 5).map(item => ({
            name: item?.farmer?.name || 'Farmer',
            kg: item?.quantity?.net_kg || 0,
            amount: item?.financials?.total_amount || 0
        }));
    }, [rawResponse]);

    const reports = rawResponse?.data || [];
    const summary = rawResponse?.summary || {};
    const pagination = rawResponse?.pagination || {};

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 bg-light min-vh-100">
                {/* --- HEADER --- */}
                <div className="d-md-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold m-0 text-dark">Purchase Analytics</h3>
                        <p className="text-muted small m-0">Procurement & Farmer Volume Analysis</p>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        <div style={{ width: '180px' }}>
                            <Select
                                options={filterOptions}
                                value={filters.filter_period}
                                onChange={handlePeriodChange}
                                styles={customSelectStyles}
                            />
                        </div>
                        <Button color="white" className="shadow-sm border rounded-pill px-3 py-2" onClick={() => loadReports()}>
                            <FiRefreshCcw />
                        </Button>
                        <Button color="primary" className="px-4 shadow-sm fw-bold rounded-pill" onClick={() => navigate("/purchase/add")}>
                            New Purchase
                        </Button>
                    </div>
                </div>

                {/* --- SUMMARY CARDS (Maintained) --- */}
                <Row className="g-3 mb-4">
                    <StatCard title="Total Procurement" value={`${summary?.total_net_kg?.toLocaleString() || 0} KG`} subValue={`${summary?.total_quintal || 0} Qtl`} icon={<FiBox />} color="#0d6efd" bg="#0d6efd15" />
                    <StatCard title="Total Purchase Value" value={`₹${summary?.total_amount?.toLocaleString() || 0}`} subValue="Gross Value" icon={<FiShoppingCart />} color="#6610f2" bg="#6610f215" />
                    <StatCard title="Pending Payments" value={`₹${summary?.total_balance?.toLocaleString() || 0}`} subValue="To be Settled" icon={<FiTrendingUp />} color="#dc3545" bg="#dc354515" />
                </Row>

                {/* MUI Custom Range Filter - Dashboard Style Ref */}
                {filters.filter_period.value === 'custom' && (
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <CardBody className="p-4">
                            <Row className="g-3 align-items-end">
                                <Col md={4}>
                                    <Label className="x-small fw-bold text-muted d-block mb-2 text-uppercase">Start Date</Label>
                                    <DatePicker
                                        disableFuture
                                        format="DD/MM/YYYY"
                                        value={filters.from_date}
                                        maxDate={dayjs()}
                                        onChange={(val) => setFilters({ ...filters, from_date: val })}
                                        sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff' } }}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label className="x-small fw-bold text-muted d-block mb-2 text-uppercase">End Date</Label>
                                    <DatePicker
                                        disableFuture
                                        format="DD/MM/YYYY"
                                        value={filters.to_date}
                                        minDate={filters.from_date}
                                        maxDate={dayjs()}
                                        onChange={(val) => setFilters({ ...filters, to_date: val })}
                                        sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff' } }}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Button color="primary" block className="fw-bold rounded-3 shadow-sm" style={{ height: '54px' }} onClick={() => loadReports()}>
                                        Sync Analytics
                                    </Button>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                )}

                {/* --- VISUALS (Maintained) --- */}
                <Row className="g-4 mb-4">
                    <Col lg={7}>
                        <Card className="border-0 shadow-sm rounded-4 h-100">
                            <CardBody className="p-4">
                                <h6 className="fw-bold mb-4 text-uppercase small text-muted">Recent Purchase Volume (KG)</h6>
                                <div style={{ width: '100%', height: 300 }}>
                                    {chartData.length > 0 ? (
                                        <ResponsiveContainer>
                                            <BarChart data={chartData}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                <XAxis dataKey="name" axisLine={false} tickLine={false} fontSize={10} />
                                                <YAxis axisLine={false} tickLine={false} fontSize={10} />
                                                <Tooltip cursor={{ fill: '#f8f9fa' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                                <Bar dataKey="kg" fill="#0d6efd" radius={[4, 4, 0, 0]} barSize={40} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : <div className="h-100 d-flex align-items-center justify-content-center text-muted italic">No procurement data found</div>}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={5}>
                        <Card className="border-0 shadow-sm rounded-4 h-100">
                            <CardBody className="p-4">
                                <h6 className="fw-bold mb-4 text-uppercase small text-muted">Filter Purchases</h6>
                                <Form onSubmit={handleSearchSubmit}>
                                    <FormGroup>
                                        <Label className="x-small fw-bold">QUICK SEARCH</Label>
                                        <div className="input-group input-group-merge shadow-none border rounded-3 overflow-hidden">
                                            <span className="input-group-text bg-white border-0"><i className="bx bx-search text-muted"></i></span>
                                            <Input
                                                className="border-0 ps-0"
                                                placeholder="Farmer name or Product..."
                                                value={filters.search}
                                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                            />
                                        </div>
                                    </FormGroup>
                                    <Button color="primary" block className="fw-bold py-2 shadow-sm mt-4">Run Report Query</Button>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* --- DATA TABLE (Maintained) --- */}
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="bg-light">
                                <tr className="text-muted small text-uppercase ls-1">
                                    <th className="ps-4 py-3">Farmer & Product</th>
                                    <th className="text-center">Quantity (Net)</th>
                                    <th className="text-center">Sacks</th>
                                    <th className="text-center">Payment Status</th>
                                    <th className="text-end pe-4">Total Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reports?.length > 0 ? (
                                    reports.map((item) => (
                                        <tr key={item?.purchase_id}>
                                            <td className="ps-4 py-3">
                                                <div className="fw-bold text-dark text-uppercase">{item?.farmer?.name}</div>
                                                <div className="d-flex flex-wrap gap-2 mt-1">
                                                    <Badge color="soft-primary" className="text-primary">{item?.product?.name}</Badge>
                                                    <small className="text-muted x-small"><FiMapPin className="me-1" />{item?.farmer?.address?.substring(0, 15)}...</small>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <div className="fw-bold text-dark">{item?.quantity?.net_kg?.toLocaleString()} <small>KG</small></div>
                                                <div className="text-muted x-small">{item?.quantity?.quintal} Qtl</div>
                                            </td>
                                            <td className="text-center">
                                                <Badge color="light" className="text-dark border rounded-pill px-3">
                                                    {item?.sacks?.count} Sacks
                                                </Badge>
                                            </td>
                                            <td className="text-center" style={{ width: '180px' }}>
                                                <div className="d-flex justify-content-between x-small mb-1">
                                                    <span className={`fw-bold text-${item?.financials?.status === 'paid' ? 'success' : 'warning'}`}>
                                                        {item?.financials?.status?.toUpperCase()}
                                                    </span>
                                                    <span className="text-muted">{item?.financials?.paid_percentage}%</span>
                                                </div>
                                                <Progress value={item?.financials?.paid_percentage} size="xs" color={item?.financials?.status === 'paid' ? 'success' : 'primary'} />
                                                <div className="x-small text-muted mt-1">Bal: ₹{item?.financials?.balance_amount?.toLocaleString()}</div>
                                            </td>
                                            <td className="text-end pe-4">
                                                <div className="fw-bold text-dark fs-6">₹{item?.financials?.total_amount?.toLocaleString()}</div>
                                                <small className="text-muted x-small">Rate: ₹{item?.pricing?.rate}/kg</small>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="5" className="py-5 text-center text-muted italic">No purchase records found.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* Pagination */}
                {pagination?.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4 pb-4">
                        <Pagination className="pagination-rounded shadow-sm">
                            <PaginationItem disabled={filters?.page <= 1}>
                                <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters?.page - 1 })} />
                            </PaginationItem>
                            {[...Array(pagination?.total_pages || 0)].map((_, i) => (
                                <PaginationItem active={i + 1 === filters?.page} key={i}>
                                    <PaginationLink onClick={() => setFilters({ ...filters, page: i + 1 })}>{i + 1}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={filters?.page >= pagination?.total_pages}>
                                <PaginationLink next onClick={() => setFilters({ ...filters, page: filters?.page + 1 })} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
};

const StatCard = ({ title, value, subValue, icon, color, bg }) => (
    <Col md={4}>
        <Card className="border-0 shadow-sm rounded-4 h-100 bg-white">
            <CardBody className="d-flex align-items-center gap-3 p-4">
                <div style={{ width: '55px', height: '55px', borderRadius: '16px', backgroundColor: bg, color: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
                    {icon}
                </div>
                <div>
                    <p className="m-0 text-muted x-small fw-bold text-uppercase ls-1">{title}</p>
                    <h3 className="m-0 fw-bold" style={{ color: '#2c3e50' }}>{value}</h3>
                    <small className="text-muted x-small">{subValue}</small>
                </div>
            </CardBody>
        </Card>
    </Col>
);

export default PurchaseReportList;